import {
	ChangeDetectionStrategy,
	Component,
	OnDestroy,
	OnInit,
	signal,
	Signal,
	inject,
} from '@angular/core';
import { FileViewerModalComponent } from '@shared/ui-components/au-file-loader/file-viewer-modal/file-viewer-modal.component';
import { MatDialog } from '@angular/material/dialog';
import { filter, takeUntil, tap } from 'rxjs/operators';
import { combineLatest, mergeMap, of, Subject } from 'rxjs';
import { CmmsPages, ICmmsState } from '@app/pages/cmms/state/cmms.reducer';
import { Store } from '@ngrx/store';
import {
	AssetMappingEntityTypes,
	ICmmsAsset,
	ICmmsAssetEditPayload,
} from '@app/pages/cmms/interfaces/assets.interfaces';
import {
	assetEntities,
	assetWorkspaceIsFromOutside,
	editedAssetPrimaryKey,
	needReloadAsset,
} from '@app/pages/cmms/components/assets/state/assets.selectors';
import { TableType } from '@app/pages/cmms/types';
import { ACTIVE_PAGE_CONFIG } from '@app/pages/cmms/configs/active-page.config';
import { SetRightSidebarComponentContext } from '@layout/state/layout.actions';
import {
	attachedEntityTableConfig,
	sidebarHeaders,
} from '@app/pages/cmms/configs/cmms-table-configs';
import { RightSidebarNavigationService } from '@layout/au-right-sidebar/right-sidebar-navigation.service';
import { iconEnum } from '@app/configs/model/IconEnum';
import { ICMMSColumn } from '@shared/ui-components/au-table-layout/utils/interfaces';
import { AuDialogService } from '@core/services/au-dialog.service';
import {
	reloadCmmsAssetsWithSavePagination,
	setAssetOutsideFlagForWorkspace,
	startDeletingCmmsAsset,
	startLinkingCmmsAsset,
	startUpdateCmmsAsset,
	turnOffForceReload,
	upsertCmmsAsset,
} from '@app/pages/cmms/components/assets/state/assets.actions';
import { AuRightSidebarService } from '@core/services/au-right-sidebar.service';
import { ICMMSLocation } from '@app/pages/cmms/interfaces/cmms-global.interfaces';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { IIncident } from '@app/pages/cmms/interfaces/incidents.interface';
import { IRequest } from '@app/pages/cmms/interfaces/request.interfaces';
import { AssetsService } from '@app/pages/cmms/services/assets.service';
import { AuErrorAdapter, AuUtilsFunctions } from '@app/shared';
import { IconSnackBarService } from '@core/services/icon-snack-bar.service';
import { IAttachedFile } from '@app/pages/cmms/interfaces/files.interfaces';
import { IClassification } from '@app/pages/cmms/interfaces/classification.interfaces';
import { RightSidebarAliasesEnum } from '@layout/au-right-sidebar/au-right-sidebar-component-aliases';
import { toSignal } from '@angular/core/rxjs-interop';
import {
	IAuPaginationData,
	IDjangoPaginationData,
} from '@shared/ui-components/au-paginator/pagination-page.info';
import { CmmsFiltersService } from '@app/pages/cmms/components/cmms-sort-filters-grouping/services/cmms-filters.service';
import { IDroppedFile } from '@shared/ui-components/au-file-loader/au-file-loader.interfaces';
import { CmmsSharedService } from '@app/pages/cmms/services/cmms-shared.service';
import { FilesService } from '@app/pages/cmms/services/files.service';
import { MatMenuModule } from '@angular/material/menu';
import { MatRippleModule } from '@angular/material/core';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { CmmsEntityNameComponent } from '../../shared-form-group-controls/cmms-entity-name/cmms-entity-name.component';
import { CmmsLocationsSectionComponent } from '../../shared-form-group-controls/cmms-locations-section/cmms-locations-section.component';
import { ImageSectionComponent } from '../../files/image-section/image-section.component';
import { CmmsDescriptionComponent } from '../../shared-form-group-controls/cmms-description/cmms-description.component';
import { CmmsLabelsSectionComponent } from '../../shared-form-group-controls/cmms-labels-section/cmms-labels-section.component';
import { MaintenanceSectionComponent } from '../../maintenance-section/maintenance-section.component';
import { AssetConditionGradeComponent } from '../asset-condition-grade/asset-condition-grade.component';
import { CmmsSimpleTextareaComponent } from '../../shared-form-group-controls/cmms-simple-textarea/cmms-simple-textarea.component';
import { AuMaintenanceSelectorComponent } from '../../au-maintenance-selector/au-maintenance-selector.component';
import { AuCmmsCostComponent } from '../../au-cmms-cost/au-cmms-cost.component';
import { CmmsSimpleInputComponent } from '../../shared-form-group-controls/cmms-simple-input/cmms-simple-input.component';
import { CmmsYearSelectorComponent } from '../../shared-form-group-controls/cmms-year-selector/cmms-year-selector.component';
import { CmmsDatePickerComponent } from '../../shared-form-group-controls/cmms-date-picker/cmms-date-picker.component';
import { CmmsCostInputComponent } from '../../shared-form-group-controls/cmms-cost-input/cmms-cost-input.component';
import { CmmsClassificationComponent } from '../../shared-form-group-controls/cmms-classification/cmms-classification.component';
import { EntitySectionComponent } from '../../entity-section/entity-section.component';
import { FileSectionComponent } from '../../files/file-section/file-section.component';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

import { ChartListRootComponent } from '@app/pages/cmms/components/assets/asset-time-series/charts-list-root/chart-list-root.component';
import { OpenDtFromWorkspaceComponent } from '@app/pages/cmms/components/shared-form-group-controls/cmms-open-dt-from-workspace/open-dt-from-workspace.component';
import { NgxTolgeeModule } from '@tolgee/ngx';
import { TranslateService } from '@tolgee/ngx';
import { timeframeMap } from '@app/pages/cmms/components/assets/models/timeframe-status';
import { IWorkOrder } from '@app/domain/cmms-types/workorder-types';
import { AiFloatingConfirmationComponent } from '@app/pages/cmms/components/ai-floating-confirmation/ai-floating-confirmation.component';
import { AiImageAnalysisLoadingComponent } from '@app/pages/cmms/components/ai-image-analysis-loading/ai-image-analysis-loading.component';
import { IAiAssetAnalysisResponse } from '@app/shared/services/image-analysis.service';
import { FeatureFlagService } from '@app/core/services/feature-flag.service';
import { UsersTeamPermissionLocationType } from '@app/pages/settings/users/interfaces/users-management.interfaces';
import {
	AttachNewQuickEntityComponent,
	ActionType,
} from '@app/shared/entity-workspace-components/quick-entity/attach-new-quick-entity/attach-new-quick-entity.component';
import { AiSearchSuggestionsComponent } from '../../ai-search-suggestions/ai-search-suggestions.component';

@Component({
	selector: 'edit-asset',
	templateUrl: './edit-asset.component.html',
	styleUrls: ['./edit-asset.component.scss'],
	providers: [CmmsFiltersService],
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [
		MatMenuModule,
		MatRippleModule,
		MatIconModule,
		MatButtonModule,
		CmmsEntityNameComponent,
		CmmsLocationsSectionComponent,
		ImageSectionComponent,
		CmmsDescriptionComponent,
		CmmsLabelsSectionComponent,
		MaintenanceSectionComponent,
		AssetConditionGradeComponent,
		CmmsSimpleTextareaComponent,
		AuMaintenanceSelectorComponent,
		AuCmmsCostComponent,
		CmmsSimpleInputComponent,
		CmmsYearSelectorComponent,
		CmmsDatePickerComponent,
		CmmsCostInputComponent,
		CmmsClassificationComponent,
		EntitySectionComponent,
		FileSectionComponent,
		MatProgressSpinnerModule,
		ChartListRootComponent,
		OpenDtFromWorkspaceComponent,
		NgxTolgeeModule,
		AiFloatingConfirmationComponent,
		AiImageAnalysisLoadingComponent,
		AttachNewQuickEntityComponent,
		AiSearchSuggestionsComponent,
	],
})
export class EditAssetComponent implements OnInit, OnDestroy {
	translateService = inject(TranslateService);
	private readonly featureFlagsService = inject(FeatureFlagService);

	assetForm: FormGroup = this.fb.group({});
	isPanelOpen = true;
	fromOutside: Signal<boolean> = this.store$.selectSignal(assetWorkspaceIsFromOutside);
	relatedFiles = signal<any[]>(null);
	paginationData = signal<IDjangoPaginationData>(null);
	showRelatedFiles = signal<boolean>(true);
	filtrationValues = {};
	totalRelatedFiles: number;
	assetAttachedFiles: IAttachedFile[] = [];
	enableMatterportIntegration = this.featureFlagsService.isFFEnabled(
		'enable-matterport-integration'
	);

	isAiAnalysisLoading = signal<boolean>(false);
	aiSuggestionActive = signal<boolean>(false);
	aiSuggestedValues = signal<Partial<ICmmsAssetEditPayload>>(null);
	aiSuggestedClassifications = signal<IClassification[]>(null);
	aiAnalysisResponse = signal<IAiAssetAnalysisResponse | null>(null);

	readonly iconEnum = iconEnum;
	readonly cmmsPages = CmmsPages;
	readonly tableType = TableType;
	readonly tableConfig: ICMMSColumn[] = attachedEntityTableConfig;
	readonly conditionGradeExpanded = signal<boolean>(false);
	readonly ActionType = ActionType;
	private destroy$: Subject<void> = new Subject<void>();
	protected readonly featureFlagService = inject(FeatureFlagService);

	asset = toSignal<ICmmsAsset>(
		combineLatest([
			this.store$.select(editedAssetPrimaryKey),
			this.store$.select(assetEntities),
			this.store$.select(needReloadAsset),
		]).pipe(
			takeUntil(this.destroy$),
			mergeMap(([pk, assets, needReloadAsset]) => {
				if (pk && assets[pk]) {
					this.assetAttachedFiles = assets[pk].assetAttachedFiles ?? [];
				}
				this.loadRelatedFiles(pk, { first: 10 });

				this.assetsService
					.loadAssetClassificationAttachedFiles(pk, { first: 10 })
					.subscribe(relatedFiles => {
						this.paginationData.set({
							totalCount: relatedFiles.totalCount,
							pageInfo: relatedFiles.pageInfo,
						});
						this.totalRelatedFiles = relatedFiles.totalCount;
						if (!this.totalRelatedFiles) {
							this.showRelatedFiles.set(false);
						}
						this.relatedFiles.set(relatedFiles.classificationAttachedFileEntities);

						if (!this.asset()) {
							return;
						}
						const attachedFilePk = this.asset().assetAttachedFiles.map(file => file.pk);
						if (this.isRelatedFile(attachedFilePk)) {
							this.relatedFiles.update(relatedFiles =>
								relatedFiles.filter(file => !attachedFilePk.includes(file.pk))
							);
						}
						return;
					});

				if (assets[pk] && !needReloadAsset) {
					this.conditionGradeExpanded.set(assets[pk].conditionGrade !== null);
					return of(assets[pk]);
				}

				this.store$.dispatch(turnOffForceReload());
				return this.assetsService.getAssetByPk(pk).pipe(
					tap(asset => {
						this.conditionGradeExpanded.set(asset.conditionGrade !== null);
					})
				);
			})
		)
	);

	constructor(
		private store$: Store<ICmmsState>,
		private sidebarNavigation: RightSidebarNavigationService,
		private dialogService: AuDialogService,
		private sidebarService: AuRightSidebarService,
		private fb: FormBuilder,
		private assetsService: AssetsService,
		private notify: IconSnackBarService,
		private dialog: MatDialog,
		private filterService: CmmsFiltersService,
		private cmmsSharedService: CmmsSharedService,
		private filesService: FilesService
	) {}

	ngOnInit(): void {
		this.trackSidebarCloseAction();
		this.subscribeToFilters();
	}

	navigateToExist(pageData: { page: CmmsPages; tableType: TableType }) {
		const asset = this.asset();
		// set point where we return
		this.sidebarNavigation.setInitialComponent({
			initialComponent: RightSidebarAliasesEnum.editAsset,
			config: {
				...ACTIVE_PAGE_CONFIG[CmmsPages.editAsset].sidebarConfig,
				title: {
					text: ACTIVE_PAGE_CONFIG[CmmsPages.editAsset].sidebarConfig.title.text,
					endfix: asset.name,
				},
			},
			initialPage: CmmsPages.editAsset,
		});

		// set context for next page
		this.store$.dispatch(
			SetRightSidebarComponentContext({
				componentContext: {
					tableTypeForExistingEntity: pageData.tableType,
					actionType: 'EDIT',
					currentEntityForEdit: asset,
					editEntityType: 'ASSET',
				},
			})
		);
		// action itself
		this.sidebarNavigation.navigateTo(
			CmmsPages.existingEntities,
			sidebarHeaders[pageData.tableType]
		);
	}

	viewSelected(item: { data: any; entityType: CmmsPages }) {
		this.sidebarService.viewCurrentEntity(
			EditAssetComponent,
			CmmsPages.editAsset,
			item,
			this.asset()
		);
	}

	assetDeleteBtnHandler() {
		const dialog = this.dialogService.openAlertDialog({
			title: this.translateService.instant('dialog_delete_asset_title'),
			description: `<p class="au-cmms-alert-text">${this.translateService.instant('dialog_delete_data_loss_warning')}</p><p class="au-cmms-alert-text">${this.translateService.instant('dialog_delete_asset_description')}</p>`,
			actionBtnText: this.translateService.instant('delete'),
		});
		const asset = this.asset();

		this.dialogService.closedByEnter(dialog).subscribe(approved => {
			if (approved) {
				this.store$.dispatch(
					startDeletingCmmsAsset({
						pk: asset.pk,
						skipTableReload: this.fromOutside(),
					})
				);
			}
		});
	}

	navigateTo(page: CmmsPages) {
		this.sidebarNavigation.setInitialComponent({
			initialComponent: RightSidebarAliasesEnum.editAsset,
			config: ACTIVE_PAGE_CONFIG[CmmsPages.editAsset].sidebarConfig,
			formValue: AuUtilsFunctions.deepCopyObject(this.assetForm.value),
			initialPage: CmmsPages.assets,
		});

		this.store$.dispatch(
			SetRightSidebarComponentContext({
				componentContext: {
					currentEntityForEdit: this.asset(),
					editEntityType: 'ASSET',
					actionType: 'EDIT',
				},
			})
		);

		this.cmmsSharedService.setExternalDataForNewComponent(page, this.asset());

		this.sidebarNavigation.navigateTo(page);
	}

	filesUpdates(files: Partial<IDroppedFile>[]) {
		//OMFG
		const mappedFiles: IAttachedFile[] = files?.map(file => {
			return {
				pk: file.pk,
				name: file.name,
				file: {
					contentType: file.contentType,
					pk: file.pk,
					url: file.url,
					name: file.name,
				},
			};
		});
		mappedFiles.forEach(file => {
			if (!file.file.url) {
				return {
					...mappedFiles,
					file: {
						...file.file,
						url: file.file.url,
					},
				};
			}
		});
		this.store$.dispatch(reloadCmmsAssetsWithSavePagination());
		const assetCopy = {
			...this.asset(),
			assetAttachedFiles: [...mappedFiles],
		};

		this.store$.dispatch(
			upsertCmmsAsset({
				asset: assetCopy,
			})
		);

		this.store$.dispatch(
			startLinkingCmmsAsset({
				assets: [this.asset().pk],
				files: files.map(file => file.pk),
			})
		);

		this.filesService.getUpdatedFiles();
		files.forEach(file => {
			const currentFile = {
				file: file.pk,
				...this.asset(),
			};
			if (file.newlyLoaded === true) {
				this.cmmsSharedService.setExternalDataForNewComponent(
					CmmsPages.files,
					currentFile
				);
			}
		});
	}

	unlinkFile({ ids, onSuccess }: { ids: string[]; onSuccess: () => void }) {
		const linkedItems = this.asset().assetAttachedFiles.filter(file => {
			return !ids.includes(file.pk);
		});

		this.assetsService
			.unlinkAssetsObjects({
				pk: this.asset().pk,
				[this.assetsService.getAssetObjectTypeByMappingType(
					AssetMappingEntityTypes.FileEntity
				)]: ids,
			})
			.subscribe({
				next: () => {
					onSuccess();
					this.assetsService.afterMappingAssetSuccess(
						this.asset(),
						AssetMappingEntityTypes.FileEntity,
						linkedItems as any[],
						true
					);
				},
				error: error => {
					this.notify.error(AuErrorAdapter.getTextFromGqlError(error));
				},
			});
	}

	unlinkRequest(request: IRequest) {
		const asset = this.asset();
		this.unlinkEntity<IRequest>(
			asset.requests as IRequest[],
			request,
			AssetMappingEntityTypes.Request,
			asset
		);
	}

	unlinkWO(wo: IWorkOrder) {
		const asset = this.asset();
		this.unlinkEntity<IWorkOrder>(
			asset.workOrders as IWorkOrder[],
			wo,
			AssetMappingEntityTypes.WorkOrder,
			asset
		);
	}

	unlinkIncident(incident: IIncident) {
		const asset = this.asset();
		this.unlinkEntity<IIncident>(
			asset.incidents as IIncident[],
			incident,
			AssetMappingEntityTypes.Incident,
			asset
		);
	}

	// TODO this can make unlink for all entities. Reuse it
	unlinkEntity<T extends IWorkOrder | IRequest | IIncident | IAttachedFile>(
		allItems: T[],
		unlinkItem: IIncident | IWorkOrder | IRequest | IAttachedFile,
		type: AssetMappingEntityTypes,
		asset: ICmmsAsset
	) {
		// TODO this a shitty code need to improve. Will be god have same as in WO
		const linkedItems = allItems.filter(item => {
			return item.pk !== unlinkItem.pk;
		});

		this.assetsService
			.unlinkAssetsObjects({
				pk: asset.pk,
				[this.assetsService.getAssetObjectTypeByMappingType(type)]: [unlinkItem.pk],
			})
			.subscribe({
				next: () => {
					this.assetsService.afterMappingAssetSuccess(asset, type, linkedItems as any[]);
				},
				error: error => {
					this.notify.error(AuErrorAdapter.getTextFromGqlError(error));
				},
			});
	}

	togglePanelState() {
		this.isPanelOpen = !this.isPanelOpen;
	}

	typeChanged(classifications: Pick<IClassification, 'pk'>[]) {
		const asset = this.asset();
		this.store$.dispatch(
			startUpdateCmmsAsset({
				asset: {
					pk: asset.pk,
					classifications: classifications.map(classification => classification.pk),
				},
				skipTableReload: this.fromOutside(),
			})
		);
	}

	trackSidebarCloseAction(): void {
		this.sidebarService.sidebarIsClosing$.pipe(takeUntil(this.destroy$)).subscribe(() => {
			this.sidebarService.closeAndClearPortal();
			if (this.fromOutside()) {
				this.store$.dispatch(setAssetOutsideFlagForWorkspace({ fromOutside: false }));
			}
		});
	}

	checkIfObjectChanged(updatedAsset: Partial<ICmmsAssetEditPayload>, asset: ICmmsAsset) {
		let hasChanges = false;
		Object.keys(updatedAsset).map(key => {
			if (updatedAsset[key] !== asset[key]) {
				hasChanges = true;
			}
		});

		return hasChanges;
	}

	editAsset(assetPayload: Partial<ICmmsAssetEditPayload>, overrideCheck = false) {
		if (this.aiSuggestedValues()) {
			this.aiSuggestedValues.update(prev => ({
				...prev,
				...assetPayload,
			}));
			return;
		}

		const asset = this.asset();
		if (!asset) {
			return;
		}

		if (this.checkIfObjectChanged(assetPayload, asset) || overrideCheck) {
			this.store$.dispatch(
				startUpdateCmmsAsset({
					asset: { pk: asset.pk, ...assetPayload },
					skipTableReload: overrideCheck ? false : this.fromOutside(),
				})
			);
		}
	}

	locationsChanged(locations: Partial<ICMMSLocation>[]) {
		if (locations.length === 0) return;

		this.editAsset({
			location: {
				id: locations[0].locationId,
				type: locations[0].locationType.toLowerCase(),
			},
		} as Partial<ICmmsAssetEditPayload>);
	}

	addChildControl(name: string, control: FormControl) {
		this.assetForm.addControl(name, control);
	}

	relatedFilesPaginationChangeHandler(pagination: IAuPaginationData) {
		this.loadRelatedFiles(this.asset()?.pk, pagination, this.filtrationValues);
	}

	openFile(fileList: IAttachedFile[], index: number) {
		return this.dialog.open(FileViewerModalComponent, {
			data: { files: fileList.map(file => file.file), index: index },
			backdropClass: 'au-modal-backdrop',
			panelClass: ['au-modal', 'no-max-width'],
		});
	}

	loadRelatedFiles(
		assetPk: string,
		pagination: IAuPaginationData,
		filterVariables: any = {}
	) {
		this.assetsService
			.loadAssetClassificationAttachedFiles(assetPk, pagination, filterVariables)
			.subscribe(relatedFiles => {
				this.paginationData.set({
					totalCount: relatedFiles.totalCount,
					pageInfo: relatedFiles.pageInfo,
				});
				this.relatedFiles.set(relatedFiles.classificationAttachedFileEntities);
				if (!this.asset()) {
					return;
				}
				const attachedFilePk = this.asset().assetAttachedFiles.map(file => file.pk);
				if (this.isRelatedFile(attachedFilePk)) {
					this.relatedFiles.update(relatedFiles =>
						relatedFiles.filter(file => !attachedFilePk.includes(file.pk))
					);
				}
				if (this.asset().assetAttachedFiles) {
					this.assetAttachedFiles = this.asset().assetAttachedFiles.filter(
						file => file.importConfirmed || !file.importId
					);
				}

				return;
			});
	}

	conditionGradeChangedHandler(status: number) {
		const timeframeValue = timeframeMap[status] || null;
		this.editAsset({ timeframe: timeframeValue, conditionGrade: status });
	}

	private isRelatedFile(filePks: string[]): boolean {
		return this.relatedFiles().some(file => filePks.includes(file.pk));
	}
	private subscribeToFilters(): void {
		this.filterService.selectedFiltersVariables$
			.pipe(takeUntil(this.destroy$))
			.subscribe(filterVariables => {
				this.filtrationValues = {
					...this.getInitalFIlterValue(),
					...filterVariables,
				};
				this.loadRelatedFiles(this.asset()?.pk, { first: 10 }, this.filtrationValues);
			});

		this.filterService.quickSearchValue$
			.pipe(
				takeUntil(this.destroy$),
				filter(searchText => searchText !== null)
			)
			.subscribe(search => {
				this.filtrationValues = { ...this.filtrationValues, search };
				this.loadRelatedFiles(this.asset()?.pk, { first: 10 }, this.filtrationValues);
			});
	}

	private getInitalFIlterValue() {
		return {
			search: '',
			type: [],
			classifications: [],
		};
	}

	reloadPage() {
		this.editAsset({ name: this.asset().name }, true);
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	handleAiAnalysisResponse = (analysisResponse: IAiAssetAnalysisResponse): void => {
		if (analysisResponse) {
			this.aiSuggestionActive.set(true);
			this.aiAnalysisResponse.set(analysisResponse);

			const suggestedPayload: Partial<ICmmsAssetEditPayload> = {
				location: {
					id: this.asset().location.id,
					type: this.asset().location.type as UsersTeamPermissionLocationType,
				},
			};

			if (analysisResponse.title) {
				suggestedPayload.name = analysisResponse.title;
			}
			if (analysisResponse.description) {
				suggestedPayload.description = analysisResponse.description;
			}
			if (analysisResponse.product_name) {
				suggestedPayload.productName = analysisResponse.product_name;
			}
			if (analysisResponse.serial_number) {
				suggestedPayload.serialNumber = analysisResponse.serial_number;
			}
			if (analysisResponse.product_year) {
				suggestedPayload.productYear = analysisResponse.product_year.toString();
			}
			if (analysisResponse.installation_date) {
				suggestedPayload.installationDate = analysisResponse.installation_date;
			}
			if (analysisResponse.installation_cost) {
				suggestedPayload.installationCost = analysisResponse.installation_cost;
			}
			if (analysisResponse.warranty_expired_date) {
				suggestedPayload.warranty = analysisResponse.warranty_expired_date;
			}
			if (analysisResponse.condition_grade) {
				suggestedPayload.conditionGrade = analysisResponse.condition_grade;
			}
			if (analysisResponse.condition_grade_description) {
				suggestedPayload.conditionGradeNote =
					analysisResponse.condition_grade_description;
			}
			if (
				analysisResponse.building_component_classification &&
				analysisResponse.building_component_classification.length > 0
			) {
				suggestedPayload.classifications =
					analysisResponse.building_component_classification.map(bct => bct?.pk);
				this.aiSuggestedClassifications.set(
					analysisResponse.building_component_classification
				);
			}

			this.aiSuggestedValues.set(suggestedPayload);
		}
	};

	handleAiAnalysisLoadingState = (isLoading: boolean): void => {
		this.isAiAnalysisLoading.set(isLoading);
	};

	acceptSuggestions(): void {
		const suggestedValuesCopy = { ...this.aiSuggestedValues() };
		this.resetAiSuggestions(); // Order is important here, as we need to reset the suggestions before editing the asset
		this.editAsset(suggestedValuesCopy);
	}

	declineSuggestions(): void {
		this.resetAiSuggestions();
	}

	private resetAiSuggestions(): void {
		this.aiSuggestionActive.set(false);
		this.aiSuggestedValues.set(null);
		this.aiSuggestedClassifications.set(null);
		this.aiAnalysisResponse.set(null);
	}

	onFilesAdded = (newFiles: IAttachedFile[]): void => {
		const existingFiles = this.asset().assetAttachedFiles || [];
		this.assetForm.patchValue({ files: [...existingFiles, ...newFiles] });
	};
}
